interface CalendarInviteInput {
  bookingId: string
  title: string
  description: string
  startTime: number
  endTime: number
  location: string
  guestEmail: string
}

/**
 * Converts a unix-ms timestamp into the `YYYYMMDDTHHmmssZ` format that the
 * iCalendar and Google Calendar URL specs both require.
 *
 * @param {number} timestamp - Unix epoch in milliseconds.
 * @returns {string} Compact ISO timestamp without separators, ending in `Z`.
 */
const toICalStamp = (timestamp: number): string =>
  new Date(timestamp).toISOString().replace(/[-:]/gu, '').replace(/\.\d{3}/u, '')

/**
 * Builds a "Add to Google Calendar" URL pre-filled with the booking details.
 *
 * @param {CalendarInviteInput} input - Booking details.
 * @returns {string} The constructed URL.
 */
const buildGoogleCalendarLink = (input: CalendarInviteInput): string => {
  const parameters = new URLSearchParams({
    action: 'TEMPLATE',
    text: input.title,
    dates: `${toICalStamp(input.startTime)}/${toICalStamp(input.endTime)}`,
    details: input.description,
    location: input.location,
  })
  return `https://calendar.google.com/calendar/render?${parameters.toString()}`
}

/**
 * Builds an iCalendar (.ics) file body as a blob URL the user can download to
 * import the booking into any calendar app. Resulting blob URL must be revoked
 * by the caller when it's no longer needed.
 *
 * @param {CalendarInviteInput} input - Booking details.
 * @returns {string} Object URL for a generated .ics file.
 */
const buildIcalBlobUrl = (input: CalendarInviteInput): string => {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//QuickCasa//QuickCal//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:booking-${input.bookingId}@book.quickcasa.ai`,
    `DTSTAMP:${toICalStamp(Date.now())}`,
    `DTSTART:${toICalStamp(input.startTime)}`,
    `DTEND:${toICalStamp(input.endTime)}`,
    `SUMMARY:${escapeIcalText(input.title)}`,
    `DESCRIPTION:${escapeIcalText(input.description)}`,
    `LOCATION:${escapeIcalText(input.location)}`,
    `ATTENDEE;CN=${escapeIcalText(input.guestEmail)}:mailto:${input.guestEmail}`,
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' })
  return URL.createObjectURL(blob)
}

const escapeIcalText = (value: string): string =>
  value.replace(/\\/gu, '\\\\').replace(/\n/gu, '\\n').replace(/,/gu, '\\,')

export { buildGoogleCalendarLink, buildIcalBlobUrl }
export type { CalendarInviteInput }
